import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const API_BASE = "https://capitaliq-backend-production-bff2.up.railway.app";

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = {
  page: { minHeight: "100vh", background: "#060b14", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" },
  card: { background: "#0c1428", border: "1px solid #172035", borderRadius: 16, padding: 40, width: "100%", maxWidth: 440 },
  logo: { display: "flex", alignItems: "center", gap: 12, marginBottom: 32 },
  logoIcon: { width: 36, height: 36, borderRadius: 9, background: "linear-gradient(135deg,#3b7df7,#6d48e5)", display: "flex", alignItems: "center", justifyContent: "center" },
  title: { color: "#fff", fontSize: 22, fontWeight: 700, letterSpacing: "-0.03em" },
  sub: { color: "#3a5570", fontSize: 13, marginTop: 2 },
  label: { color: "#4a6888", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6, display: "block" },
  input: { background: "#111828", border: "1.5px solid #1c2d45", borderRadius: 10, padding: "11px 14px", color: "#fff", fontSize: 14, fontFamily: "'DM Sans', sans-serif", width: "100%", outline: "none", boxSizing: "border-box" },
  btn: { width: "100%", padding: "12px", borderRadius: 10, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#3b7df7,#6d48e5)", color: "#fff", fontSize: 15, fontWeight: 600, marginTop: 8 },
  btnGhost: { width: "100%", padding: "10px", borderRadius: 10, border: "1px solid #1c2d45", cursor: "pointer", background: "transparent", color: "#4a6888", fontSize: 13, fontWeight: 500, marginTop: 8 },
  err: { background: "#1a0505", border: "1px solid #ef444430", borderRadius: 8, padding: "10px 14px", color: "#ef4444", fontSize: 13, marginBottom: 16 },
  info: { background: "#051020", border: "1px solid #3b7df730", borderRadius: 8, padding: "10px 14px", color: "#7aabf8", fontSize: 13, marginBottom: 16 },
  gap: { height: 16 },
  // Dashboard styles
  dash: { minHeight: "100vh", background: "#060b14", fontFamily: "'DM Sans', sans-serif" },
  header: { background: "#0c1428", borderBottom: "1px solid #172035", padding: "16px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  dashTitle: { color: "#fff", fontSize: 18, fontWeight: 700 },
  content: { padding: 32, maxWidth: 1100, margin: "0 auto" },
  sectionTitle: { color: "#fff", fontSize: 16, fontWeight: 700, marginBottom: 16, fontFamily: "'Playfair Display', serif" },
  lenderCard: { background: "#0c1428", border: "1px solid #172035", borderRadius: 14, padding: 20, marginBottom: 16 },
  lenderHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  lenderName: { color: "#fff", fontSize: 15, fontWeight: 700 },
  badge: (active) => ({ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: active ? "#052010" : "#1a0505", color: active ? "#22c55e" : "#ef4444", border: `1px solid ${active ? "#22c55e40" : "#ef444440"}` }),
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 },
  field: { background: "#070f1e", border: "1px solid #1c2d45", borderRadius: 8, padding: 12 },
  fieldLabel: { color: "#4a6888", fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 4 },
  fieldInput: { background: "transparent", border: "none", color: "#fff", fontSize: 13, fontFamily: "'DM Sans', sans-serif", width: "100%", outline: "none" },
  toggle: (active) => ({ width: 44, height: 24, borderRadius: 12, background: active ? "linear-gradient(135deg,#3b7df7,#6d48e5)" : "#111828", border: `1px solid ${active ? "#3b7df7" : "#1c2d45"}`, cursor: "pointer", position: "relative", transition: "all 0.2s" }),
  toggleDot: (active) => ({ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: active ? 22 : 2, transition: "left 0.2s" }),
  saveBtn: { padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#3b7df7,#6d48e5)", color: "#fff", fontSize: 12, fontWeight: 600 },
  savedMsg: { color: "#22c55e", fontSize: 12, fontWeight: 600 },
};

// ─── Login Step 1: Email + Password ──────────────────────────────────────────
function LoginStep({ onMFARequired, onError, error }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
  setLoading(true);
  const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
  setLoading(false);
  if (err) { onError(err.message); return; }
  if (data?.session) {
    onMFARequired();
  } else {
    onError('Login failed. Please try again.');
  }
};

  return (
    <div style={s.card}>
      <div style={s.logo}>
        <div style={s.logoIcon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
          </svg>
        </div>
        <div>
          <div style={s.title}>CapitalIQ Admin</div>
          <div style={s.sub}>Lender Matrix Management</div>
        </div>
      </div>
      {error && <div style={s.err}>{error}</div>}
      <div style={s.info}>🔒 This area is restricted. Unauthorised access is prohibited.</div>
      <div>
        <label style={s.label}>Email</label>
        <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="guymorgan@iqfins.co" onKeyDown={e => e.key === 'Enter' && handleLogin()} />
      </div>
      <div style={s.gap} />
      <div>
        <label style={s.label}>Password</label>
        <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} />
      </div>
      <button style={s.btn} onClick={handleLogin} disabled={loading}>
        {loading ? "Signing in…" : "Continue →"}
      </button>
    </div>
  );
}

// ─── Login Step 2: TOTP 2FA ───────────────────────────────────────────────────
function MFAStep({ onSuccess, onError, error }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [factors, setFactors] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [factorId, setFactorId] = useState("");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data }) => {
      const totp = data?.totp || [];
      setFactors(totp);
      if (totp.length === 0) startEnrolment();
      else setChecking(false);
    });
  }, []);

  const startEnrolment = async () => {
    setEnrolling(true);
    setChecking(false);
    const { data, error: err } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      issuer: 'CapitalIQ',
      friendlyName: 'CapitalIQ Admin'
    });
    if (err) { onError(err.message); return; }
    setQrCode(data.totp.qr_code);
    setFactorId(data.id);
  };

  const handleEnrolVerify = async () => {
    setLoading(true);
    const { data: challengeData, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId });
    if (challengeErr) { setLoading(false); onError(challengeErr.message); return; }
    const { error: verifyErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code
    });
    setLoading(false);
    if (verifyErr) { onError("Invalid code. Please try again."); return; }
    onSuccess();
  };

  const handleVerify = async () => {
    if (!factors.length) { onError("No MFA factors found."); return; }
    setLoading(true);
    const fId = factors[0].id;
    const { data: challengeData, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId: fId });
    if (challengeErr) { setLoading(false); onError(challengeErr.message); return; }
    const { error: verifyErr } = await supabase.auth.mfa.verify({
      factorId: fId,
      challengeId: challengeData.id,
      code
    });
    setLoading(false);
    if (verifyErr) { onError("Invalid code. Please try again."); return; }
    onSuccess();
  };

  if (checking) return (
    <div style={{...s.card, textAlign:'center', color:'#4a6888'}}>
      Checking MFA status…
    </div>
  );

  if (enrolling && qrCode) return (
    <div style={s.card}>
      <div style={s.logo}>
        <div style={s.logoIcon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>
          </svg>
        </div>
        <div>
          <div style={s.title}>Set Up Authenticator</div>
          <div style={s.sub}>Scan with Google Authenticator</div>
        </div>
      </div>
      {error && <div style={s.err}>{error}</div>}
      <div style={s.info}>📱 Open Google Authenticator → tap + → Scan QR code</div>
      <div style={{ textAlign: 'center', margin: '16px 0' }}>
        <img src={qrCode} alt="QR Code" style={{ width: 200, height: 200, borderRadius: 8 }} />
      </div>
      <div>
        <label style={s.label}>Enter the 6-digit code to confirm</label>
        <input
          style={{...s.input, fontSize: 24, letterSpacing: "0.3em", textAlign: "center"}}
          type="text" inputMode="numeric" maxLength={6} value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          onKeyDown={e => e.key === 'Enter' && handleEnrolVerify()}
          placeholder="000000"
        />
      </div>
      <button style={s.btn} onClick={handleEnrolVerify} disabled={loading || code.length !== 6}>
        {loading ? "Verifying…" : "Confirm & Enable 2FA →"}
      </button>
    </div>
  );

  return (
    <div style={s.card}>
      <div style={s.logo}>
        <div style={s.logoIcon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>
          </svg>
        </div>
        <div>
          <div style={s.title}>Two-Factor Auth</div>
          <div style={s.sub}>Enter your authenticator code</div>
        </div>
      </div>
      {error && <div style={s.err}>{error}</div>}
      <div style={s.info}>📱 Open Google Authenticator and enter the 6-digit code for CapitalIQ.</div>
      <div>
        <label style={s.label}>Authentication Code</label>
        <input
          style={{...s.input, fontSize: 24, letterSpacing: "0.3em", textAlign: "center"}}
          type="text" inputMode="numeric" maxLength={6} value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          onKeyDown={e => e.key === 'Enter' && handleVerify()}
          placeholder="000000"
        />
      </div>
      <button style={s.btn} onClick={handleVerify} disabled={loading || code.length !== 6}>
        {loading ? "Verifying…" : "Verify & Enter →"}
      </button>
    </div>
  );
}

// ─── Lender Rule Fields ───────────────────────────────────────────────────────
const RULE_FIELDS = [
  { key: "min_years_trading", label: "Min Years Trading", type: "number" },
  { key: "max_amount", label: "Max Loan Amount ($)", type: "number" },
  { key: "max_asset_age_primary", label: "Max Asset Age — Primary (yrs)", type: "number" },
  { key: "max_asset_age_secondary", label: "Max Asset Age — Secondary (yrs)", type: "number" },
  { key: "max_asset_age_tertiary", label: "Max Asset Age — Tertiary (yrs)", type: "number" },
  { key: "max_asset_age_passenger", label: "Max Asset Age — Passenger (yrs)", type: "number" },
  { key: "min_years_gst", label: "Min Years GST", type: "number" },
  { key: "min_years_prime_mover", label: "Min Years (Prime Mover)", type: "number" },
  { key: "fast_track_threshold", label: "Fast Track Threshold ($)", type: "number" },
  { key: "startup_max_amount", label: "Start-Up Max Amount ($)", type: "number" },
  { key: "startup_min_months", label: "Start-Up Min Months", type: "number" },
  { key: "agri_min_years_gst", label: "Agri Min Years GST", type: "number" },
  { key: "agri_max_amount", label: "Agri Max Amount ($)", type: "number" },
  { key: "truck_max_amount", label: "Truck Max Amount ($)", type: "number" },
  { key: "max_amount_no_property", label: "Max Amount (No Property) ($)", type: "number" },
  { key: "deposit_required_above", label: "Deposit Required Above ($)", type: "number" },
  { key: "deposit_percentage", label: "Deposit Percentage (%)", type: "number" },
  { key: "credit_ref_required_above", label: "Credit Ref Required Above ($)", type: "number" },
  { key: "credit_ref_months", label: "Credit Ref Months", type: "number" },
];

const BOOL_FIELDS = [
  { key: "accepts_ato_debt", label: "Accepts ATO Debt" },
  { key: "accepts_sole_trader", label: "Accepts Sole Traders" },
  { key: "accepts_passenger_vehicle", label: "Accepts Passenger Vehicles" },
  { key: "accepts_prime_mover", label: "Accepts Prime Movers" },
  { key: "requires_property", label: "Requires Property" },
  { key: "requires_gst", label: "Requires GST" },
];

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ onSignOut }) {
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [drafts, setDrafts] = useState({});

  useEffect(() => { fetchLenders(); }, []);

  const fetchLenders = async () => {
    setLoading(true);
    const { data } = await supabase.from('lenders').select('*, lender_rules(*)').order('created_at');
    setLenders(data || []);
    const d = {};
    (data || []).forEach(l => { d[l.id] = { ...l.lender_rules?.[0] }; });
    setDrafts(d);
    setLoading(false);
  };

  const updateDraft = (lenderId, key, value) => {
    setDrafts(prev => ({ ...prev, [lenderId]: { ...prev[lenderId], [key]: value } }));
  };

  const toggleActive = async (lender) => {
    await supabase.from('lenders').update({ active: !lender.active }).eq('id', lender.id);
    setLenders(prev => prev.map(l => l.id === lender.id ? { ...l, active: !l.active } : l));
  };

  const saveRules = async (lender) => {
    setSaving(prev => ({ ...prev, [lender.id]: true }));
    const draft = drafts[lender.id];
    const ruleId = lender.lender_rules?.[0]?.id;
    if (ruleId) {
      await supabase.from('lender_rules').update(draft).eq('id', ruleId);
    }
    setSaving(prev => ({ ...prev, [lender.id]: false }));
    setSaved(prev => ({ ...prev, [lender.id]: true }));
    setTimeout(() => setSaved(prev => ({ ...prev, [lender.id]: false })), 2000);
  };

  return (
    <div style={s.dash}>
      <div style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={s.logoIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div style={s.dashTitle}>CapitalIQ — Lender Matrix</div>
        </div>
        <button onClick={onSignOut} style={{ background: "none", border: "1px solid #1c2d45", color: "#4a6888", padding: "7px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
          Sign out
        </button>
      </div>

      <div style={s.content}>
        <div style={{ ...s.sectionTitle, marginBottom: 24 }}>
          Active Lender Panel — {lenders.filter(l => l.active).length} of {lenders.length} active
        </div>

        {loading && <div style={{ color: "#4a6888", fontSize: 14 }}>Loading lenders…</div>}

        {lenders.map(lender => {
          const draft = drafts[lender.id] || {};
          return (
            <div key={lender.id} style={s.lenderCard}>
              <div style={s.lenderHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={s.lenderName}>{lender.name}</div>
                  <div style={{ color: "#4a6888", fontSize: 12 }}>{lender.display_name}</div>
                  <span style={s.badge(lender.active)}>{lender.active ? "Active" : "Inactive"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {saved[lender.id] && <span style={s.savedMsg}>✅ Saved!</span>}
                  <button style={s.saveBtn} onClick={() => saveRules(lender)} disabled={saving[lender.id]}>
                    {saving[lender.id] ? "Saving…" : "Save Changes"}
                  </button>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: "#4a6888", fontSize: 12 }}>{lender.active ? "On" : "Off"}</span>
                    <div style={s.toggle(lender.active)} onClick={() => toggleActive(lender)}>
                      <div style={s.toggleDot(lender.active)} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Boolean fields */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
                {BOOL_FIELDS.map(f => (
                  <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 8, background: "#070f1e", border: "1px solid #1c2d45", borderRadius: 8, padding: "8px 12px" }}>
                    <span style={{ color: "#7aabf8", fontSize: 12 }}>{f.label}</span>
                    <div style={s.toggle(!!draft[f.key])} onClick={() => updateDraft(lender.id, f.key, !draft[f.key])}>
                      <div style={s.toggleDot(!!draft[f.key])} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Numeric fields */}
              <div style={s.grid}>
                {RULE_FIELDS.map(f => (
                  <div key={f.key} style={s.field}>
                    <div style={s.fieldLabel}>{f.label}</div>
                    <input style={s.fieldInput} type="number" value={draft[f.key] ?? ''} onChange={e => updateDraft(lender.id, f.key, parseFloat(e.target.value) || 0)} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Admin Page ──────────────────────────────────────────────────────────
export default function AdminPage() {
  const [step, setStep] = useState("login"); // login | mfa | dashboard
  const [error, setError] = useState("");
  const [session, setSession] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) {
        supabase.auth.mfa.getAuthenticatorAssuranceLevel().then(({ data: aal }) => {
          if (aal?.currentLevel === 'aal2') {
            setSession(data.session);
            setStep("dashboard");
          }
        });
      }
    });
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setStep("login");
    setSession(null);
    setError("");
  };

  if (step === "dashboard") return <Dashboard onSignOut={handleSignOut} />;

  return (
    <div style={s.page}>
      {step === "login" && (
        <LoginStep
          error={error}
          onError={setError}
          onMFARequired={() => { setError(""); setStep("mfa"); }}
        />
      )}
      {step === "mfa" && (
        <MFAStep
          error={error}
          onError={setError}
          onSuccess={() => { setError(""); setStep("dashboard"); }}
        />
      )}
    </div>
  );
}
